// CHA-141 — AI Usage: what the tenant's AI calls used and roughly cost, month by month, read from the
// CHA-140 ledger (tenants/{tenantId}/agent_audit).
//
// Platform and tenant-agnostic: the shell passes `tenantId`; this panel never guesses one. Reads go
// through the client SDK under the existing rules (tenant admins and platform roles may read the
// ledger; nobody may write it from a browser). There is no write path here. Costs are the per-call
// estimates stored on each record — the provider's invoice is the bill.
//
// Tenant/month switch safety (the CHA-616 lesson, as in useRunEvents): results are kept with the
// tenant and month they were read for and shown only while both still match, and a load that
// finishes after the inputs changed is ignored.
import React, { useEffect, useMemo, useState } from 'react';
import { getFirestore, collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { monthRange, currentMonth, summarizeUsage, dailySeries, usageCsv } from '../aiUsage/aiUsage';

// Single-field range + order on startedAt: served by Firestore's automatic index, no composite needed.
export async function loadAuditRecords(tenantId, start, end) {
  const db = getFirestore();
  const q = query(
    collection(db, 'tenants', tenantId, 'agent_audit'),
    where('startedAt', '>=', start), where('startedAt', '<', end), orderBy('startedAt'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ auditId: d.id, ...d.data() }));
}

const usd = (v) => (v >= 0.01 || v === 0 ? `$${v.toFixed(2)}` : `$${v.toFixed(4)}`);
const int = (v) => Number(v || 0).toLocaleString('en-US');
const labelCls = 'font-plex-mono text-[10px] uppercase tracking-[0.08em] text-carbon-gray-60';
const btnCls = 'font-plex-mono text-[10px] uppercase tracking-[0.08em] border border-carbon-gray-30 bg-carbon-white px-2.5 py-1 text-carbon-gray-70 hover:text-carbon-ink disabled:opacity-40';

function Stat({ label, value, sub, testId }) {
  return (
    <div className="border border-carbon-border bg-carbon-white px-3 py-2" data-testid={testId}>
      <div className={labelCls}>{label}</div>
      <div className="text-[18px] font-semibold tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-[11px] text-carbon-gray-60 mt-0.5">{sub}</div>}
    </div>
  );
}

function Breakdown({ title, rows, testId }) {
  return (
    <div className="border border-carbon-border bg-carbon-white" data-testid={testId}>
      <div className={`${labelCls} px-3 py-2 border-b border-carbon-border`}>{title}</div>
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left text-carbon-gray-60">
            <th className="px-3 py-1.5 font-medium">Name</th>
            <th className="px-3 py-1.5 font-medium text-right">Calls</th>
            <th className="px-3 py-1.5 font-medium text-right">Errors</th>
            <th className="px-3 py-1.5 font-medium text-right">Tokens in / out</th>
            <th className="px-3 py-1.5 font-medium text-right">Est. cost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-carbon-border" data-testid={`${testId}-${r.key}`}>
              <td className="px-3 py-1.5 font-plex-mono">{r.key}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{int(r.calls)}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{int(r.errors)}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{int(r.inputTokens)} / {int(r.outputTokens)}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{usd(r.costUsd)}{r.unpriced ? ` + ${r.unpriced} unpriced` : ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AiUsagePanel({ tenantId, load = loadAuditRecords, now }) {
  const [month, setMonth] = useState(() => currentMonth(now || new Date()));
  const [state, setState] = useState({ tenantId: null, month: null, records: [], error: null });

  useEffect(() => {
    const range = monthRange(month);
    if (!tenantId || !range) return undefined;
    let current = true;
    load(tenantId, range.start, range.end).then(
      (records) => { if (current) setState({ tenantId, month, records, error: null }); },
      (err) => {
        if (!current) return;
        console.error('AI usage load failed', err);
        setState({ tenantId, month, records: [], error: 'Could not load AI usage for this month.' });
      },
    );
    return () => { current = false; };
  }, [tenantId, month, load]);

  const fresh = state.tenantId === tenantId && state.month === month;
  const records = useMemo(() => (fresh ? state.records : []), [fresh, state.records]);
  const summary = useMemo(() => summarizeUsage(records), [records]);
  const days = useMemo(() => dailySeries(records, month), [records, month]);

  if (!tenantId) return null;

  const exportCsv = () => {
    const blob = new Blob([usageCsv(records)], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ai-usage_${tenantId}_${month}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(a.href);
  };

  const t = summary.totals;
  return (
    <div className="font-plex text-carbon-ink space-y-4" data-testid="ai-usage-panel">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h2 className="text-[15px] font-semibold m-0">AI Usage</h2>
          <p className="text-[12px] text-carbon-gray-70 mt-0.5 mb-0">
            Every AI call made with this tenant&apos;s key. Costs are estimates from list prices; your AI provider&apos;s invoice is the bill. Months and days are UTC.
          </p>
        </div>
        <label className="ml-auto">
          <span className={`${labelCls} block mb-0.5`}>Month</span>
          <input type="month" aria-label="Month" value={month} onChange={(e) => monthRange(e.target.value) && setMonth(e.target.value)}
            className="border border-carbon-border bg-carbon-white px-2 py-1 text-[12px]" />
        </label>
        <button type="button" className={btnCls} onClick={exportCsv} disabled={!fresh || !records.length}>Export CSV</button>
      </div>

      {!fresh && <div className="text-[12px] text-carbon-gray-60" role="status">Loading…</div>}
      {fresh && state.error && <div className="text-[12px] text-carbon-red-60" role="alert">{state.error}</div>}
      {fresh && !state.error && !records.length && (
        <div className="text-[12px] text-carbon-gray-60 border border-carbon-border bg-carbon-gray-10 px-3 py-4 text-center">No AI calls in {month}.</div>
      )}

      {fresh && records.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat testId="stat-calls" label="Calls" value={int(t.calls)} sub={t.errors ? `${int(t.errors)} failed` : 'none failed'} />
            <Stat testId="stat-cost" label="Est. cost" value={usd(t.costUsd)} sub={t.unpriced ? `${int(t.unpriced)} call${t.unpriced === 1 ? '' : 's'} unpriced` : 'all calls priced'} />
            <Stat testId="stat-tokens" label="Tokens in / out" value={`${int(t.inputTokens)} / ${int(t.outputTokens)}`} />
            <Stat testId="stat-cache" label="Cache tokens" value={int(t.cacheTokens)} sub="written + read" />
          </div>

          <div className="border border-carbon-border bg-carbon-white px-3 py-2" data-testid="daily-chart">
            <div className={labelCls}>Est. cost per day</div>
            <div style={{ width: '100%', height: 180 }}>
              <ResponsiveContainer>
                <BarChart data={days} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" tickFormatter={(d) => d.slice(8)} tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={48} tickFormatter={(v) => usd(v)} />
                  <Tooltip formatter={(v) => usd(v)} labelFormatter={(d) => d} />
                  <Bar dataKey="costUsd" name="Est. cost" fill="#0f62fe" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-3">
            <Breakdown title="By feature" rows={summary.byFeature} testId="by-feature" />
            <Breakdown title="By model (as served)" rows={summary.byModel} testId="by-model" />
          </div>
        </>
      )}
    </div>
  );
}
